// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from 'router';

// store
import { apiUrl } from 'store/api';
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';

// hooks
import { useAuth, useApp, useCareCircle, useOnboardingV1 } from 'hooks';

// components
import { AppStore } from '@karehero/llama/components/organisms/AppStore/AppStore';
import { Dialog } from '@karehero/llama/components/atoms/Dialog/Dialog';

import LoadingPage from '@karehero/llama/components/pages/Loading/Loading';
import OnboardingFlow from '@karehero/llama/components/pages/OnboardingFlow/OnboardingFlow';

// posthog
import { useFeatureFlags } from 'hooks/useFeatureFlags';

// models
import { MemberRole, Role } from '@karehero/models';

// capacitor
import { Capacitor } from '@capacitor/core';

/**
 * OnboardingMemberCreate takes the user through the onboarding flow for a care concierge.
 */
export const OnboardingMemberCreateV1 = () => {
  // hooks
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const { isApp } = useApp();
  const { isAuthenticated, isLoading } = useAuth();
  const account = useSelector(getAccount);
  const { careCircle } = useCareCircle();

  // careCircleMember from useCareCircle is always the primary caregiver,
  // so we find the logged-in user's own member record from the care circle.
  const currentUserMember = useMemo(
    () =>
      careCircle?.careCircleMembers?.find((m) => m.account.id === account.id),
    [careCircle, account.id],
  );

  const isPrimaryCaregiver = useMemo(
    () =>
      currentUserMember?.roles.some(
        (r: Role) => r.name === MemberRole.PrimaryCaregiver,
      ) ?? true,
    [currentUserMember],
  );

  const {
    introConfig,
    feelingConfig,
    typeformConfig,
    onboardingV1Config,
    updateIsOnboarded,
  } = useOnboardingV1({
    typeformID: account?.organization?.typeformID,
  });

  // state
  const [isAppStoreOpen, setIsAppStoreOpen] = useState(!isApp);
  const [isTypeformSkip, setIsTypeformSkip] = useState(false);

  // methods
  // set search params
  const handleNext = useCallback(
    (configID: string, subConfigID: string) => {
      setSearchParams({ configID, subConfigID });
    },
    [setSearchParams],
  );

  // complete
  const handleComplete = useCallback(async () => {
    await updateIsOnboarded();
    navigate(ROUTES.ACTION_PLAN);
    window.location.reload();
  }, [updateIsOnboarded, navigate]);

  // effects
  useEffect(() => {
    if (searchParams.get('utm_source') === 'karehero_typeform') {
      setIsTypeformSkip(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) navigate(ROUTES.SIGN_IN);
  }, [navigate, isAuthenticated, isLoading]);

  // memos
  const defaultConfig = useMemo(() => [...introConfig], [introConfig]);

  const primaryCaregiverConfig = useMemo(
    () => [
      ...(isTypeformSkip
        ? []
        : [
            ...introConfig,
            ...(flags.onboardingTypeform && account?.organization?.typeformID
              ? typeformConfig
              : [...onboardingV1Config]),
          ]),
      ...feelingConfig,
    ],
    [
      introConfig,
      typeformConfig,
      onboardingV1Config,
      feelingConfig,
      flags,
      isTypeformSkip,
    ],
  );

  const onboardingConfig = useMemo(() => {
    if (!currentUserMember) return defaultConfig;
    return isPrimaryCaregiver ? primaryCaregiverConfig : [];
  }, [
    currentUserMember,
    isPrimaryCaregiver,
    defaultConfig,
    primaryCaregiverConfig,
  ]);

  // Non-primary caregivers (invited care circle members) skip onboarding entirely.
  useEffect(() => {
    if (currentUserMember && onboardingConfig.length === 0) {
      handleComplete();
    }
  }, [onboardingConfig, handleComplete, currentUserMember]);

  return isAuthenticated ? (
    <>
      <OnboardingFlow
        config={onboardingConfig}
        onNext={handleNext}
        onComplete={handleComplete}
      />
      {flags.appStoreSignpost && (
        <Dialog
          variant='full'
          isOpen={isAppStoreOpen}
          setIsOpen={setIsAppStoreOpen}
        >
          <AppStore storeUrl={`${apiUrl}/v1/app-store`} />
        </Dialog>
      )}
    </>
  ) : (
    <LoadingPage />
  );
};

OnboardingMemberCreateV1.displayName = 'OnboardingMemberCreateV1';
export default OnboardingMemberCreateV1;
